// frontend/src/pages/admin/HandbookAcknowledgements.js
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle2, Clock, Users, Search, ArrowLeft } from 'lucide-react';
import { useHandbookAckDashboard } from '../../hooks/useHandbookData';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'acknowledged', label: 'Acknowledged' },
];

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

const HandbookAcknowledgements = () => {
  const { data, isLoading } = useHandbookAckDashboard();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const employees = data?.employees || [];
  const summary = data?.summary || { total: 0, acknowledged: 0, pending: 0, percent: 0 };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      if (filter === 'pending' && e.acknowledged) return false;
      if (filter === 'acknowledged' && !e.acknowledged) return false;
      if (!q) return true;
      return [e.name, e.staffId, e.email, e.department]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [employees, filter, search]);

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link to="/admin/handbook" aria-label="Back to handbook editor" className="icon-button flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Handbook Acknowledgements</h1>
            <p className="text-xs sm:text-sm text-gray-600">
              {data?.edition ? `Version ${data.edition.code} - effective ${data.edition.effectiveDate}` : 'Compliance tracking'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Active employees" value={summary.total} tone="bg-gray-100 text-gray-600" />
        <StatCard icon={CheckCircle2} label="Acknowledged" value={summary.acknowledged} tone="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Clock} label="Pending" value={summary.pending} tone="bg-amber-100 text-amber-600" />
        <StatCard icon={CheckCircle2} label="Completion" value={`${summary.percent}%`} tone="bg-blue-100 text-blue-600" />
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-medium text-gray-700">Acknowledgement progress</span>
          <span className="text-gray-500">{summary.acknowledged} of {summary.total}</span>
        </div>
        <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${summary.percent}%` }} />
        </div>
      </div>

      {/* Filters + table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${filter === f.id ? 'bg-red-50 text-red-700' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, staff ID, department..."
              className="input-admin w-full pl-9"
              aria-label="Search employees"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Employee</th>
                  <th className="table-header-cell">Department</th>
                  <th className="table-header-cell text-center">Status</th>
                  <th className="table-header-cell">Acknowledged at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((e) => (
                  <tr key={e.staffId || e.email}>
                    <td className="px-6 py-3">
                      <p className="text-sm font-medium text-gray-900">{e.name}</p>
                      <p className="text-xs text-gray-500">{e.staffId}{e.email ? ` - ${e.email}` : ''}</p>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{e.department || '-'}</td>
                    <td className="px-6 py-3 text-center">
                      {e.acknowledged ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledged
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          <Clock className="w-3.5 h-3.5" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{fmt(e.acknowledgedAt)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-400">No employees match this view.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandbookAcknowledgements;
